import type { PlanId, PlanPeriod } from '../plans'
import { supabase } from './supabase'
import type { Features, Role, TenantType } from '../types'

/**
 * The system administrator's own operations on institutions already open.
 * Opening one is the signup's job now (/kayit); the panel's own "new
 * institution" form was removed at the user's request (11 Oct 2026).
 *
 * Listing and editing tenants go straight through the Supabase client, RLS
 * already lets the system administrator touch every institution
 * (`is_superadmin()` makes `is_owner(id)` true for all of them, see
 * supabase/superadmin.sql). Anything that touches `auth.users` needs the
 * service-role key, so those calls go to functions/admin instead.
 *
 * Supabase-only, deliberately. The system administrator screen only exists
 * once the app has already moved to Supabase, there is no Firebase
 * counterpart to keep in step with, unlike services/staff.ts.
 */

export interface Tenant {
  id: string
  name: string
  display_name: string
  currency: string
  timezone: string
  student_discount_percent: number
  type: TenantType
  /** Which optional parts this institution has been given. */
  features: Features
  created_at: string
  /** Free-trial end (ISO), null for a permanent institution. */
  trial_ends_at: string | null
  /** Contact number from signup, `+90…`; null for older institutions. */
  contact_phone: string | null
  /** Patronun hesap silme talebi (ISO); doluysa kurum kapalı (supabase/hesap-silme.sql). */
  deletion_requested_at: string | null
  /** Paket ('start' | 'plus' | 'max', plans.ts); paketsizse null. */
  plan: PlanId | null
  /** Paketin ödeme dönemi; bilinmiyorsa null. */
  plan_period: PlanPeriod | null
}

export async function listTenants(): Promise<Tenant[]> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  const { data, error } = await supabase
    .from('tenants')
    .select(
      'id, name, display_name, currency, timezone, student_discount_percent, type, features, created_at, trial_ends_at, contact_phone, deletion_requested_at, plan, plan_period',
    )
    .order('created_at', { ascending: false })

  if (error) throw new Error(error.message)
  // Rows written before these columns existed carry nulls; the app's own
  // defaults are the same ones the database column defaults declare.
  return (data ?? []).map((r) => ({
    ...r,
    type: 'restaurant',
    features: (r.features as Features) ?? {},
  })) as Tenant[]
}

export async function updateTenant(
  id: string,
  patch: Partial<
    Pick<
      Tenant,
      | 'name'
      | 'display_name'
      | 'currency'
      | 'timezone'
      | 'student_discount_percent'
      | 'type'
      | 'features'
      | 'trial_ends_at'
      | 'plan'
      | 'plan_period'
    >
  >,
): Promise<void> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  const { error } = await supabase.from('tenants').update(patch).eq('id', id)
  if (error) throw new Error(error.message)
}

export interface StaffMember {
  uid: string
  username: string
  /** Blank when never set, the edit form has to see that, not a stand-in. */
  displayName: string
  role: Role
}

/**
 * Every account in an institution, owner included.
 *
 * Straight through RLS, same reasoning as listTenants: is_superadmin() makes
 * is_member() true for every tenant, so a plain select sees every row.
 */
export async function listStaff(tenantId: string): Promise<StaffMember[]> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  const { data, error } = await supabase
    .from('admins')
    .select('id, username, display_name, role')
    .eq('tenant_id', tenantId)
    .order('role')

  if (error) throw new Error(error.message)
  return (data ?? []).map((r) => ({
    uid: r.id,
    username: r.username,
    // Not falling back to the username here: the panel edits this field, and
    // a stand-in shown in the box would be saved as if it were the real name.
    displayName: r.display_name ?? '',
    role: r.role as Role,
  }))
}

/** Shared by every call into functions/admin, same error-unwrapping each needs. */
async function invokeAdmin(body: Record<string, unknown>): Promise<void> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')

  const { data, error } = await supabase.functions.invoke('admin', { body })

  if (error) {
    const detail = (await error.context?.json?.().catch(() => null)) as
      | { error?: string }
      | null
    throw new Error(detail?.error ?? 'İşlem tamamlanamadı.')
  }
  if (data?.error) throw new Error(String(data.error))
}

/**
 * Corrects the name shown against an owner's row, a label, nothing more.
 *
 * Credentials are deliberately not here. An owner holds a real address now,
 * so a forgotten password is answered by the reset mail on the sign-in
 * screen; setting one from this panel would mean knowing somebody else's
 * password, which is worse than the inconvenience it saves.
 */
export async function renameOwner(
  panelSecret: string,
  uid: string,
  displayName: string,
): Promise<void> {
  await invokeAdmin({
    action: 'rename-owner',
    panelSecret,
    targetId: uid,
    displayName,
  })
}

/**
 * Closes an institution, its staff logins, menu, categories, orders, all of
 * it. Irreversible, which is why it goes through functions/admin rather than
 * a plain RLS delete: the tenant row itself could be removed straight from
 * the browser (superadmin.sql's `tenants_delete` allows it), but that would
 * leave every staff member's auth.users row behind, still able to sign in
 * to a kurum the app no longer shows anyone. The function deletes those
 * first. PANEL_SECRET is checked on the server the same way createOwner's is.
 */
/** Silme talebinden vazgeçen kurumu yeniden açar: hesapların kilidi kalkar. */
export async function restoreTenant(panelSecret: string, tenantId: string): Promise<void> {
  await invokeAdmin({ action: 'restore-tenant', panelSecret, tenantId })
}

export async function deleteTenant(panelSecret: string, tenantId: string): Promise<void> {
  await invokeAdmin({ action: 'delete-tenant', panelSecret, tenantId })
}

/** Days left on a trial (negative once over), or null for a permanent one. */
export function trialDaysLeft(t: Pick<Tenant, 'trial_ends_at'>): number | null {
  if (!t.trial_ends_at) return null
  return Math.ceil((Date.parse(t.trial_ends_at) - Date.now()) / 86_400_000)
}

/** A "Sizi arayalım" request left on the marketing site. */
export interface Lead {
  id: string
  /** "arama": tanıtım sitesinden "Sizi arayalım"; "devam": denemedeki patrondan. */
  kind: 'arama' | 'devam'
  tenant_id: string | null
  name: string
  business: string
  phone: string | null
  note: string
  handled: boolean
  created_at: string
}

/** Newest first. Only the system administrator can read these (RLS). */
export async function listLeads(): Promise<Lead[]> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  const { data, error } = await supabase
    .from('leads')
    .select('id, kind, tenant_id, name, business, phone, note, handled, created_at')
    .order('created_at', { ascending: false })
  if (error) throw new Error(error.message)
  return (data ?? []) as Lead[]
}

export async function setLeadHandled(id: string, handled: boolean): Promise<void> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  const { error } = await supabase.from('leads').update({ handled }).eq('id', id)
  if (error) throw new Error(error.message)
}

/** Talebi kalıcı olarak siler (RLS: yalnızca sistem yöneticisi). */
export async function deleteLead(id: string): Promise<void> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  const { error } = await supabase.from('leads').delete().eq('id', id)
  if (error) throw new Error(error.message)
}

/**
 * The institution's owner and their sign-in address, for the tenant form's
 * contact lines. Null when the institution has no owner yet.
 */
export async function getOwnerContact(
  tenantId: string,
): Promise<{ name: string; email: string } | null> {
  if (!supabase) throw new Error('Supabase yapılandırılmamış')
  const { data, error } = await supabase
    .from('admins')
    .select('display_name, username, email')
    .eq('tenant_id', tenantId)
    .eq('role', 'owner')
    .limit(1)
    .maybeSingle()
  if (error) throw new Error(error.message)
  if (!data) return null
  return { name: data.display_name || data.username, email: data.email ?? '' }
}

/** Kurumun etkinliği (supabase/kurum-etkinligi.sql), yalnız sistem yöneticisine. */
export interface TenantActivity {
  /** Uygulamanın en son kullanıldığı an (oturum yenilenmesi ya da giriş). */
  seen: string | null
  /** Şifreyle son giriş. */
  login: string | null
  lastOrder: string | null
  sales7d: number
  /** En son sipariş açılan, ürün eklenen ya da ödeme alınan an. */
  lastAction: string | null
  /** Kayıtlı kişi hesapları (patron, kasiyer, garson); fonksiyon eskiyse null. */
  users: number | null
  /** Bunlardan şu an uygulamayı kullananlar (son 1 saat); fonksiyon eskiyse null. */
  liveUsers: number | null
}

export async function listActivity(): Promise<Record<string, TenantActivity>> {
  if (!supabase) return {}
  const { data, error } = await supabase.rpc('tenant_activity')
  if (error) throw new Error(error.message)
  const out: Record<string, TenantActivity> = {}
  for (const r of (data ?? []) as {
    tenant_id: string
    son_gorulme: string | null
    son_giris: string | null
    son_satis: string | null
    satis_7gun: number
    son_islem: string | null
    kullanici_toplam?: number
    kullanici_canli?: number
  }[]) {
    const seen = [r.son_gorulme, r.son_giris].filter(Boolean).sort().pop() ?? null
    out[r.tenant_id] = {
      seen,
      login: r.son_giris,
      lastOrder: r.son_satis,
      sales7d: r.satis_7gun,
      lastAction: r.son_islem,
      users: r.kullanici_toplam ?? null,
      liveUsers: r.kullanici_canli ?? null,
    }
  }
  return out
}

export async function listStaffActivity(
  tenantId: string,
): Promise<Record<string, { seen: string | null; login: string | null }>> {
  if (!supabase) return {}
  const { data, error } = await supabase.rpc('tenant_staff_activity', { t: tenantId })
  if (error) throw new Error(error.message)
  const out: Record<string, { seen: string | null; login: string | null }> = {}
  for (const r of (data ?? []) as { user_id: string; son_gorulme: string | null; son_giris: string | null }[]) {
    const seen = [r.son_gorulme, r.son_giris].filter(Boolean).sort().pop() ?? null
    out[r.user_id] = { seen, login: r.son_giris }
  }
  return out
}
